import {
  ESC_POS, line, separator, padLine, wrapText, buildReceipt, getCharsPerLine, wrapWords,
  type PaperWidth,
} from "./thermal-printer";
import { linhasDoRodape, normalizarRodape } from "./receipt-footer";

function formatCurrency(value: number): string {
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

function formatDate(dateStr: string, timezone?: string | null): string {
  // Renderiza no fuso DA LOJA (stores.timezone; default BRT) independente do
  // ambiente: backend roda em UTC, frontend pode estar em qualquer
  // fuso. Usar getDate()/getHours() puxava o fuso local do processo.
  return new Date(dateStr)
    .toLocaleString("pt-BR", {
      timeZone: timezone || "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(", ", " ");
}

/** A taxa de serviço do pedido para o cupom: «Taxa de serviço (10%)» e o valor; sem taxa, null. */
export function taxaDeServico(order: { taxaServico?: number | string | null; taxaServicoPct?: number | string | null }): { rotulo: string; valor: number } | null {
  const valor = order.taxaServico != null ? parseFloat(String(order.taxaServico)) : 0;
  if (!(valor > 0)) return null;
  const pct = order.taxaServicoPct != null ? parseFloat(String(order.taxaServicoPct)) : 0;
  const pctTexto = pct > 0 ? ` (${String(Math.round(pct * 100) / 100).replace(".", ",")}%)` : "";
  return { rotulo: `Taxa de serviço${pctTexto}`, valor };
}

// WhatsApp do cliente no cupom (os 3 renderizadores). Sem número de verdade não há linha: o PDV grava
// `__avulso__<loja>` (balcão) ou `__avulso__<loja>_<ts>` (cliente salvo sem número) no lugar do número, e o cupom
// imprimia o identificador cru. Mesma regra do painel (temWhatsApp): `__avulso__` ou menos de 10 dígitos.
export function receiptWhatsapp(raw: string | null | undefined): string | undefined {
  if (!raw || String(raw).startsWith("__avulso__")) return undefined;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length < 10) return undefined;
  if (digits.length === 13 && digits.startsWith("55")) {
    const ddd = digits.slice(2, 4);
    const part1 = digits.slice(4, 9);
    const part2 = digits.slice(9);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

export interface ReceiptOrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  // Preço-base do produto (order_items.price, por unidade). 0 com opção de preço = o preço mora na opção (ver
  // receiptPrecoNaOpcao). Ausente nos cupons montados à mão (teste, mesa): conta como base com valor.
  basePrice?: number | null;
  options?: Array<{ optionName: string; price: number; groupName?: string | null }>;
  removedIngredients?: string[];
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  // Encomenda por turno (12/10/2026): 'manha' | 'tarde' | 'noite'. Com horário, o horário vence.
  scheduledShift?: string | null;
  notes?: string | null;
  // Venda por peso: kg pesados (unitPrice já é o valor calculado da linha).
  weightKg?: number | null;
  // Como a loja vende (order_items.weight_unit, T-0056): 'kg' | 'g' | 'ml'; nulo = kg. O peso continua em kg.
  weightUnit?: string | null;
  // "1 de 2" quando a linha foi quebrada em unidades. Ver expandItemUnits.
  unitLabel?: string | null;
  // Produto com pacote de escolhas: "Marmitas: 8½ (7 do pacote + 2 extras)". Ver receiptPrepLine.
  prepLine?: string | null;
  // «Cada lista conta separado» (28/10/2026): a conta de cada lista vai no título do grupo dela («Adicionais - Copo 1:
  // 3 (2 no preço + 1 extra)»), com o adicional embaixo do seu copo. Título do grupo → conta.
  prepPorGrupo?: Record<string, string> | null;
}

/** O título do grupo no cupom, com a conta da lista quando ela conta separado. */
export function receiptGroupTitle(item: Pick<ReceiptOrderItem, "prepPorGrupo">, title: string): string {
  const conta = item.prepPorGrupo?.[title];
  return conta ? `${title}: ${conta}` : `${title}:`;
}

/**
 * A linha do pacote de escolhas do item (15/10/2026), do retrato gravado em order_items.prep_count. Mesmo texto de
 * prepCountLine em @workspace/pricing (o cupom não depende do pricing), com o nome que a loja dá aos itens:
 * "Marmitas: 8½ (7 do pacote + 2 extras)" e, até os inclusos (abaixo só no PDV), "Marmitas: 5 de 7". Retrato sem o
 * nome (antes de 15/10/2026, todos da Personal Chef) sai "Preparos". «Até N no preço» (`required: false`):
 * "Ingredientes: 2 de 3 no preço" / "Ingredientes: 4 (3 no preço + 1 extra)".
 */
/** A conta sem o nome: "8½ (7 do pacote + 2 extras)", "2 de 3 no preço" (o mesmo prepContaTexto do pricing). */
function receiptPrepConta(total: number, included: number, extras: number, noPreco: boolean): string {
  const fmt = (n: number) => {
    const inteiro = Math.floor(n + 1e-9);
    if (n - inteiro <= 0.25) return String(inteiro);
    return inteiro === 0 ? "½" : `${inteiro}½`;
  };
  if (total <= included + 1e-9) return `${fmt(total)} de ${fmt(included)}${noPreco ? " no preço" : ""}`;
  return `${fmt(total)} (${fmt(included)} ${noPreco ? "no preço" : "do pacote"}${extras > 0 ? ` + ${extras} ${extras === 1 ? "extra" : "extras"}` : ""})`;
}

/** «Cada lista conta separado»: nome da lista → conta, do retrato gravado. Null fora desse pacote. */
export function receiptPrepPorGrupo(c: { listas?: unknown; required?: unknown } | null | undefined): Record<string, string> | null {
  if (!c || !Array.isArray(c.listas) || c.listas.length === 0) return null;
  const out: Record<string, string> = {};
  for (const l of c.listas as any[]) {
    const total = Number(l?.total), included = Number(l?.included);
    if (!l?.nome || !Number.isFinite(total) || !Number.isFinite(included)) continue;
    out[String(l.nome)] = receiptPrepConta(total, included, Number(l?.extras) || 0, c.required === false);
  }
  return out;
}

export function receiptPrepLine(c: { total?: unknown; included?: unknown; extras?: unknown; unitName?: unknown; required?: unknown; listas?: unknown } | null | undefined): string | null {
  if (!c) return null;
  // «Cada lista conta separado» (28/10/2026): uma linha por lista, com o nome dela, separadas por \n (quem imprime quebra).
  if (Array.isArray(c.listas) && c.listas.length > 0) {
    const linhas = c.listas
      .map((l: any) => receiptPrepLine({ total: l?.total, included: l?.included, extras: l?.extras, unitName: l?.nome, required: c.required }))
      .filter((l): l is string => !!l);
    return linhas.length ? linhas.join("\n") : null;
  }
  const total = Number(c.total);
  const included = Number(c.included);
  const extras = Number(c.extras) || 0;
  if (!Number.isFinite(total) || !Number.isFinite(included)) return null;
  const fmt = (n: number) => {
    const inteiro = Math.floor(n + 1e-9);
    if (n - inteiro <= 0.25) return String(inteiro);
    return inteiro === 0 ? "½" : `${inteiro}½`;
  };
  const palavra = c.unitName == null ? "preparos" : String(c.unitName).replace(/\s+/g, " ").trim() || "itens";
  const nome = palavra[0]!.toUpperCase() + palavra.slice(1);
  const noPreco = c.required === false;
  if (total <= included + 1e-9) return `${nome}: ${fmt(total)} de ${fmt(included)}${noPreco ? " no preço" : ""}`;
  return `${nome}: ${fmt(total)} (${fmt(included)} ${noPreco ? "no preço" : "do pacote"}${extras > 0 ? ` + ${extras} ${extras === 1 ? "extra" : "extras"}` : ""})`;
}

export function formatWeightKg(w: number): string {
  return w.toFixed(3).replace(".", ",");
}

/** O peso no cupom: kg como sempre («1,500 kg»); g e ml em número inteiro («250 g», «5 ml»; 1 ml = 1 g = 0,001 kg). */
export function receiptWeight(w: number, unidade?: string | null): string {
  if (unidade === "g" || unidade === "ml") return `${Math.round(w * 1000)} ${unidade}`;
  return `${formatWeightKg(w)} kg`;
}

// Linha do crediário ("Parcelado: 5x · 1ª em 10/08") — null sem parcelas.
export function receiptCrediarioLine(order: ReceiptOrder): string | null {
  const n = order.crediarioInstallments ?? 0;
  if (!n || n < 1) return null;
  const due = order.crediarioFirstDue;
  const dueLabel =
    due && /^\d{4}-\d{2}-\d{2}$/.test(due)
      ? ` · 1a em ${due.slice(8, 10)}/${due.slice(5, 7)}`
      : "";
  return `Parcelado: ${n}x${dueLabel}`;
}

// Cabeçalho do cupom: orderCode pré-formatado vence; dailyOrderNumber cobre
// payload antigo sem o campo novo; sem nenhum dos dois, só "Pedido".
// Data da encomenda UMA vez no cabeçalho quando é a mesma em todos os itens agendados (data única no carrinho,
// 05/10/2026); com datas diferentes (pedido antigo, PDV com data por item) continua uma linha por item.
export function receiptDataUnica(items: ReceiptOrderItem[]): { data: string; hora: string | null; turno: string | null } | null {
  const agendados = items.filter((i) => i.scheduledDate);
  if (agendados.length === 0) return null;
  const a = agendados[0]!;
  const igual = agendados.every(
    (i) =>
      i.scheduledDate === a.scheduledDate &&
      (i.scheduledTime ?? null) === (a.scheduledTime ?? null) &&
      (i.scheduledTime ? true : (i.scheduledShift ?? null) === (a.scheduledShift ?? null)),
  );
  if (!igual) return null;
  const [, m, d] = a.scheduledDate!.split("-");
  return { data: `${d}/${m}`, hora: a.scheduledTime ?? null, turno: a.scheduledTime ? null : a.scheduledShift ?? null };
}

const NOME_TURNO_CUPOM: Record<string, string> = { manha: "Manhã", tarde: "Tarde", noite: "Noite" };

// Complemento da data da encomenda no cupom: " as 15:00" (ESC/POS e texto, sem acento como sempre), " às 15:00"
// (navegador) ou " - Tarde"; só a data = "".
export function receiptQuandoEncomenda(hora: string | null | undefined, turno: string | null | undefined, html = false): string {
  if (hora) return html ? ` às ${hora}` : ` as ${hora}`;
  if (turno && NOME_TURNO_CUPOM[turno]) return ` - ${NOME_TURNO_CUPOM[turno]}`;
  return "";
}

export function receiptOrderLabel(order: ReceiptOrder): string {
  if (order.orderCode) return `Pedido #${order.orderCode}`;
  if (order.dailyOrderNumber != null)
    return `Pedido #${String(order.dailyOrderNumber).padStart(4, "0")}`;
  return "Pedido";
}

// Linha "Modalidade:" nos 3 modos. "combinar" = loja sem entrega e sem retirada (Pedro, 19/10/2026).
export function receiptModeLabel(order: ReceiptOrder): string {
  if (order.deliveryMode === "entrega") return "ENTREGA";
  if (order.deliveryMode === "combinar") return "A COMBINAR";
  return "RETIRADA";
}

export interface ReceiptOrder {
  id: string;
  // Linhas em branco no topo (comandeira da cozinha cobre o começo do papel).
  topMargin?: number | null;
  dailyOrderNumber?: number | null;
  // Identificador EXIBIDO já formatado ("0012" ou "A1B2"), calculado pelo
  // orderToReceipt conforme o estilo da loja. Vence o dailyOrderNumber nos
  // formatters; o número segue no payload como fallback de agente antigo.
  orderCode?: string | null;
  createdAt: string;
  customerName: string;
  customerWhatsapp?: string;
  // «É para presente?» (Ultra): nome de quem recebe, em negrito no bloco do cliente, depois do WhatsApp dele. Sem ele
  // o cupom não muda.
  giftRecipient?: string | null;
  deliveryMode: string;
  paymentMethod: string;
  items: ReceiptOrderItem[];
  subtotal: number;
  deliveryFee: number;
  /** Frete ainda não fechado pelo lojista: `deliveryFee` 0 aqui é "a calcular",
   * não "Gratis". Sem isto o cupom promete entrega grátis num pedido que ainda
   * vai ser cobrado. */
  deliveryFeePending?: boolean;
  total: number;
  address?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    zipCode?: string;
    // Ponto de referência do cliente ("próximo à padaria") — quem usa é o
    // motoboy, então sai no cupom logo abaixo do endereço.
    reference?: string | null;
  } | null;
  notes?: string | null;
  needsChange?: boolean;
  changeFor?: number | string | null;
  cashReceived?: number | string | null;
  cardSurcharge?: number | string | null;
  // Taxa de serviço do «Fechar conta» do PDV (já no total) e a porcentagem.
  taxaServico?: number | string | null;
  taxaServicoPct?: number | string | null;
  couponCode?: string | null;
  couponDiscount?: number | string | null;
  // Programa de pontos: quanto os prêmios descontaram e os nomes deles ("Frete grátis"). Brinde sai como item.
  loyaltyDiscount?: number | string | null;
  loyaltyLabel?: string | null;
  // Pagamento misto: partes (método + valor). paymentMethod vem "Misto".
  paymentParts?: Array<{ method: string; amount: number }> | null;
  // Crediário: nº de parcelas e 1ª data (YYYY-MM-DD) quando a venda é a prazo.
  crediarioInstallments?: number | null;
  crediarioFirstDue?: string | null;
  // Fuso da loja (IANA) pra data/hora impressa; ausente = America/Sao_Paulo.
  timezone?: string | null;
  // Mesa/comanda do PDV. Sai em destaque no cupom quando presente.
  tableNumber?: string | null;
  // «COMANDA» com o «1 QR code para cada cliente» ligado (comanda individual); senão «MESA».
  tableLabel?: string | null;
  // Comanda individual: a mesa onde o cliente está (Pedro, 29/10/2026). Sai grande, embaixo da comanda.
  mesaDoCliente?: string | null;
  // Loja ligou o campo "Pedido Pago? [ ]" (stores.receipt_paid_checkbox).
  showPaidCheckbox?: boolean;
  // «Letra alta» (pdvSettings.receiptFont): o corpo sai em altura dupla com a largura normal; separadores e o que já
  // sai grande ficam como estão. Vale nos 3 modos do formatReceiptBytes; o texto puro (Text-Only) não muda.
  receiptFont?: "normal" | "alta" | null;
  // Mensagem de aniversário (cliente comprando NO dia, loja com o toggle
  // ligado) — sai centralizada antes do "Obrigado pela preferência!".
  birthdayMessage?: string | null;
  // Forma de retirada escolhida ("Uber Flash"); null = balcão. Sai colada na
  // Modalidade: "RETIRADA - Uber Flash".
  pickupMethod?: string | null;
  // Nome já substituído em birthdayMessage — permite quebrar a linha ANTES do
  // nome inteiro quando a frase não cabe (nome nunca é partido no meio).
  birthdayName?: string | null;
  // «Mensagem no rodapé do cupom» da loja (Profissional; ver receipt-footer.ts), já normalizada: TROCA o «Obrigado pela
  // preferência!» (Pedro, 23/10/2026: «esse texto que tem que ser personalizável»), na mesma posição e no mesmo
  // estilo. Sem ela, sai o Obrigado de sempre, byte a byte.
  footerMessage?: string | null;
  // Papel da cozinha (Pedro, 31/10/2026: «Não precisa imprimir o preço… é só para a cozinha preparar e levar para a
  // mesa»): pedido de mesa/comanda com a conta aberta («Pendente») sai sem preço, sem totais, sem pagamento e sem
  // modalidade. O «Imprimir conta» do fechamento tem o formatador dele e continua com os valores.
  semValores?: boolean;
}

// Linhas lógicas da mensagem de aniversário: cabe em `cols` = 1 linha; não
// cabe = quebra antes do NOME inteiro (pedido do Pedro, 21/08). Cada linha
// ainda passa pelo wrap normal depois, caso continue comprida.
export function birthdayMessageLines(msg: string, name: string | null | undefined, cols: number): string[] {
  if (msg.length <= cols || !name) return [msg];
  const i = msg.indexOf(name);
  if (i <= 0) return [msg];
  const antes = msg.slice(0, i).trimEnd();
  const resto = msg.slice(i).trim();
  return antes ? [antes, resto] : [msg];
}

// Campo pra loja marcar de caneta quando o pedido já foi pago — o motoboy vê no
// papel e não cobra de novo (antes escreviam "PAGO" à mão). Opt-in por loja e só
// na ENTREGA: na retirada quem recebe é o balcão. Sai colado no TOTAL, alinhado
// à direita, porque é ali que a loja e o motoboy olham.
export const PAID_CHECKBOX_LABEL = "Pedido Pago? [   ]";

export function receiptShowsPaidCheckbox(order: ReceiptOrder): boolean {
  return order.showPaidCheckbox === true && order.deliveryMode === "entrega";
}

// Agrupa as opções pelo título do grupo de adicionais (preservando a ordem de
// seleção) e conta repetições da mesma opção dentro do grupo. Pedidos antigos
// sem groupName caem num grupo de título vazio, que sai sem cabeçalho.
//
// `quantity` é a quantidade do ITEM: os adicionais valem por unidade, então uma
// linha "2x Burger" com 1 carne extra são DUAS carnes extras — foi o que a
// cliente pagou (7,99 x 2). Sem multiplicar aqui, o cupom mandava a cozinha
// fazer uma carne só e a mensagem do WhatsApp não fechava com o total
// (relato do Terraço 29 Burguer, pedido de 03/08/2026).
export function groupOptionsByTitle(
  options: Array<{ optionName: string; price: number; groupName?: string | null }>,
  quantity = 1,
): Array<{ title: string; options: Array<{ name: string; price: number; count: number }> }> {
  const groups: Array<{ title: string; byName: Map<string, { name: string; price: number; count: number }> }> = [];
  const byTitle = new Map<string, (typeof groups)[number]>();
  for (const opt of options) {
    const title = String(opt.groupName ?? "").trim();
    let g = byTitle.get(title);
    if (!g) {
      g = { title, byName: new Map() };
      byTitle.set(title, g);
      groups.push(g);
    }
    let o = g.byName.get(opt.optionName);
    if (!o) {
      o = { name: opt.optionName, price: opt.price, count: 0 };
      g.byName.set(opt.optionName, o);
    }
    o.count++;
  }
  const mult = Math.max(1, Math.round(quantity) || 1);
  return groups.map((g) => ({
    title: g.title,
    options: [...g.byName.values()].map((o) => ({ ...o, count: o.count * mult })),
  }));
}

/**
 * Item com adicional e quantidade > 1 vira UM BLOCO POR UNIDADE ("1 de 2").
 *
 * Sem isso, "2x Burger" com "+ 2x carne extra" fica idêntico a "1x Burger" com
 * "+ 2x carne extra": a cozinha não tem como saber se são duas carnes num
 * lanche só ou uma em cada. Relato do Terraço 29 Burguer (03/08/2026), onde o
 * lojista leu errado o próprio pedido e disse à cliente que ela tinha pedido
 * um adicional só — ela tinha pedido (e pago) dois.
 *
 * Item SEM adicional continua junto: "4x Açaí" é uma linha, não quatro.
 */
export function expandItemUnits(items: ReceiptOrderItem[]): ReceiptOrderItem[] {
  const out: ReceiptOrderItem[] = [];
  for (const item of items) {
    const n = Math.max(1, Math.round(item.quantity) || 1);
    const temAdicional = Array.isArray(item.options) && item.options.length > 0;
    if (n > 1 && temAdicional) {
      for (let i = 0; i < n; i++) {
        out.push({ ...item, quantity: 1, subtotal: item.unitPrice, unitLabel: `${i + 1} de ${n}` });
      }
    } else {
      out.push(item);
    }
  }
  return out;
}

/**
 * O preço mora na opção: produto de preço-base R$ 0 com opção de preço («Pizza Frango Recheado» + «Pequena R$ 32,00»).
 * Como no WhatsApp (Pedro, 31/10/2026, T-0003, Pizzaria Potiguar), o item sai SEM valor e a opção sai «+ Pequena
 * R$ 32,00», sem o «+» antes do preço: com o valor no item e «+R$ 32,00» embaixo, parecia somar duas vezes.
 */
export function receiptPrecoNaOpcao(item: ReceiptOrderItem): boolean {
  if (item.basePrice == null || item.basePrice !== 0) return false;
  if (item.weightKg != null && item.weightKg > 0) return false;
  const opcoes = item.options ?? [];
  if (!opcoes.some((o) => (Number(o.price) || 0) > 0)) return false;
  // Só quando as opções SOMAM o preço do item. Pizza meio a meio cobra a média (ou o maior) dos sabores: «R$ 45,00» e
  // «R$ 55,00» sem o «R$ 50,00» do item pareceriam R$ 100 — ali o valor fica no item, como antes.
  const soma = opcoes.reduce((s, o) => s + (Number(o.price) || 0), 0);
  return Math.abs(soma - item.unitPrice) < 0.005;
}

/** O preço da opção no cupom: « +R$ 5,00» (adicional que soma) ou « R$ 32,00» (o preço mora na opção). */
export function receiptPrecoDaOpcao(item: ReceiptOrderItem, valor: number): string {
  return receiptPrecoNaOpcao(item) ? ` ${formatCurrency(valor)}` : ` +${formatCurrency(valor)}`;
}

/** Rótulo da linha do item: "2x Açaí" ou "Australiano Supreme (1 de 2)". */
export function receiptItemLabel(item: ReceiptOrderItem): string {
  return item.unitLabel ? `${item.name} (${item.unitLabel})` : `${item.quantity}x ${item.name}`;
}

// Troco se aplica quando o pagamento (ou uma das partes do misto) é Dinheiro.
function receiptHasCash(order: ReceiptOrder): boolean {
  if (order.paymentMethod === "Dinheiro") return true;
  return Array.isArray(order.paymentParts) && order.paymentParts.some((p) => p.method === "Dinheiro");
}

// Valor devido EM DINHEIRO: no Misto é só a soma das partes "Dinheiro" — o
// troco compara o entregue com isso, não com o total (comparar com o total
// inflava o troco a devolver ou o deixava negativo/suprimido no Misto).
export function receiptCashDue(order: ReceiptOrder): number {
  if (order.paymentMethod === "Misto" && Array.isArray(order.paymentParts)) {
    return order.paymentParts
      .filter((p) => p.method === "Dinheiro")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  }
  return order.total;
}

// Linhas em branco antes do cabeçalho — clamp defensivo (0-8) porque o valor
// vem do banco e papel é consumível.
export function receiptTopMarginLines(order: ReceiptOrder): number {
  const n = Math.trunc(Number(order.topMargin) || 0);
  return Math.min(Math.max(n, 0), 8);
}

/**
 * Como o nome da loja entra no cabecalho: em corpo duplo, em corpo normal, ou
 * quebrado. O cabecalho sai em DOUBLE_ON, que vale DOIS caracteres por coluna
 * — entao o espaco real e `bigCols` (metade), 16 num 58mm.
 *
 * A regra antiga quebrava por CONTAGEM DE PALAVRAS ("menos de 3 palavras, nao
 * mexe") e nunca olhava largura. Nome de 2 palavras passava inteiro e QUEM
 * quebrava era a impressora, no meio da palavra: "COMERCIAL FILADELFIA" virou
 * "COMERCIAL FILADL" + "FIA" (relato d5cc40cb, 22/08/2026). Medido nos nomes de
 * producao, 158 das 939 lojas com impressora caiam nisso.
 *
 * Agora e por LARGURA, e o nome fica sempre GRANDE, como o TOTAL (Pedro,
 * 27/10/2026: «Tem que ficar grande e destacado igual o total»; de 22/08 a
 * 27/10 o nome que nao cabia numa linha grande encolhia pro corpo normal):
 * cabe numa linha grande -> uma linha; nao cabe -> grande em mais linhas,
 * quebrando ENTRE palavras (a quebra e nossa, nunca a da impressora). So uma
 * palavra maior que a linha grande cai pro corpo normal, porque grande ela
 * seria cortada no meio (3 das 203 lojas que imprimiram em 30 dias, no 58mm).
 *
 * `bigCols === cols` desliga o corpo duplo (modo texto puro nao tem "grande"):
 * a escada degrada sozinha pra "linhas de ate `cols`, quebrando entre palavras".
 */
export function storeNameHeader(
  storeName: string,
  cols: number,
  bigCols: number,
): { big: boolean; lines: string[] } {
  // trim + colapso de espaco: nome com espaco sobrando no fim contava caractere
  // e derrubava pro caso de baixo ("Friburgs gourmet " tem 17, cabia em 16).
  const name = storeName.trim().replace(/\s+/g, " ");
  if (name.length <= bigCols) return { big: true, lines: [name] };
  if (name.split(" ").every((w) => w.length <= bigCols)) return { big: true, lines: wrapWords(name, bigCols) };
  if (name.length <= cols) return { big: false, lines: [name] };
  return { big: false, lines: wrapWords(name, cols) };
}

function storeNameHeaderBytes(storeName: string, width: PaperWidth, corpo: Uint8Array = ESC_POS.DOUBLE_OFF): Uint8Array[] {
  const cols = getCharsPerLine(width);
  const header = storeNameHeader(storeName, cols, Math.floor(cols / 2));
  const out: Uint8Array[] = [];
  if (header.big) out.push(ESC_POS.DOUBLE_ON);
  for (const l of header.lines) out.push(line(l));
  if (header.big) out.push(corpo);
  return out;
}

export function formatReceipt(
  storeName: string,
  order: ReceiptOrder,
  width: PaperWidth
): Uint8Array {
  const topLeft = receiptOrderLabel(order);
  // Letra alta: o corpo fica em altura dupla. Onde o cupom voltava ao normal, volta para ela, e os separadores saem na
  // altura normal. Desligada, os bytes são os de sempre.
  const alta = order.receiptFont === "alta";
  const corpo = alta ? ESC_POS.TALL_ON : ESC_POS.DOUBLE_OFF;
  const sep = (char?: string): Uint8Array[] =>
    alta ? [ESC_POS.DOUBLE_OFF, separator(width, char), ESC_POS.TALL_ON] : [separator(width, char)];

  const parts: (Uint8Array | Uint8Array[])[] = [
    ESC_POS.INIT,
    ESC_POS.KANJI_OFF,
    ESC_POS.CODEPAGE_CP850,

    ...Array.from({ length: receiptTopMarginLines(order) }, () => line("")),
    ...(alta ? [ESC_POS.TALL_ON] : []),

    ESC_POS.ALIGN_CENTER,
    ESC_POS.BOLD_ON,
    storeNameHeaderBytes(storeName, width, corpo),
    ESC_POS.BOLD_OFF,
    ESC_POS.LF,

    sep(),

    ESC_POS.ALIGN_LEFT,
    ESC_POS.BOLD_ON,
    padLine(topLeft, formatDate(order.createdAt, order.timezone), width),
    ESC_POS.BOLD_OFF,
  ];

  if (order.tableNumber) {
    parts.push(ESC_POS.BOLD_ON, ESC_POS.DOUBLE_ON, line(`${order.tableLabel || "MESA"} ${order.tableNumber}`));
    if (order.mesaDoCliente) parts.push(line(`MESA ${order.mesaDoCliente}`));
    parts.push(corpo, ESC_POS.BOLD_OFF);
  }

  parts.push(line(`Cliente: ${order.customerName}`));

  if (order.customerWhatsapp) {
    parts.push(line(`WhatsApp: ${order.customerWhatsapp}`));
  }
  // Depois do WhatsApp do comprador: logo abaixo do presente, o número pareceria ser de quem recebe.
  if (order.giftRecipient) parts.push(ESC_POS.BOLD_ON, line(`Presente para: ${order.giftRecipient}`), ESC_POS.BOLD_OFF);

  const dataUnica = receiptDataUnica(order.items);
  if (dataUnica) {
    parts.push(ESC_POS.BOLD_ON, line(`Encomenda: ${dataUnica.data}${receiptQuandoEncomenda(dataUnica.hora, dataUnica.turno)}`), ESC_POS.BOLD_OFF);
  }

  parts.push(sep());

  for (const [idx, item] of order.items.entries()) {
    if (order.semValores || receiptPrecoNaOpcao(item)) parts.push(ESC_POS.BOLD_ON, ...wrapText(receiptItemLabel(item), width), ESC_POS.BOLD_OFF);
    else parts.push(ESC_POS.BOLD_ON, padLine(receiptItemLabel(item), formatCurrency(item.subtotal), width), ESC_POS.BOLD_OFF);

    if (item.weightKg != null && item.weightKg > 0) {
      parts.push(line(`   Peso: ${receiptWeight(item.weightKg, item.weightUnit)}`));
    }

    if (!order.semValores && item.unitPrice !== item.subtotal / item.quantity) {
      parts.push(line(`   Un: ${formatCurrency(item.unitPrice)}`));
    }

    if (item.prepLine) for (const l of item.prepLine.split("\n")) parts.push(line(`   ${l}`));

    let hadGroupTitle = false;
    if (item.options && item.options.length > 0) {
      for (const grp of groupOptionsByTitle(item.options, item.quantity)) {
        if (grp.title) {
          hadGroupTitle = true;
          parts.push(ESC_POS.LF, ESC_POS.BOLD_ON, line(`   ${receiptGroupTitle(item, grp.title)}`), ESC_POS.BOLD_OFF);
        }
        for (const g of grp.options) {
          const priceStr = !order.semValores && g.price > 0 ? receiptPrecoDaOpcao(item, g.price * g.count) : "";
          const nomeOpc = `${g.count > 1 ? `${g.count}x ` : ''}${g.name}`;
          if (`   + ${nomeOpc}${priceStr}`.length > getCharsPerLine(width)) {
            for (const l of linhasDeOpcao(nomeOpc, priceStr, getCharsPerLine(width))) parts.push(line(l));
          } else parts.push(line(`   + ${nomeOpc}${priceStr}`));
        }
      }
    }

    if (item.removedIngredients && item.removedIngredients.length > 0) {
      parts.push(line(`   Sem: ${item.removedIngredients.join(", ")}`));
    }

    if (item.scheduledDate && !dataUnica) {
      const [, m, d] = item.scheduledDate.split("-");
      const dateStr = `${d}/${m}`;
      parts.push(line(`   Encomenda: ${dateStr}${receiptQuandoEncomenda(item.scheduledTime, item.scheduledShift)}`));
    }

    if (item.notes) {
      parts.push(...wrapText(`   Obs: ${item.notes}`, width));
    }

    // Uma linha em branco entre os itens (Pedro, 31/10/2026, T-0003, opção A: «separar os itens do cupom»); depois do
    // último, só o respiro de antes do item com títulos de grupo.
    if (idx < order.items.length - 1 || hadGroupTitle) parts.push(ESC_POS.LF);
  }

  // Sem os valores (papel da cozinha), o próximo bloco (observações ou rodapé) já abre com o separador.
  if (!order.semValores) {
    parts.push(sep());

    parts.push(padLine("Subtotal:", formatCurrency(order.subtotal), width));

    const couponNum = order.couponDiscount != null
      ? (typeof order.couponDiscount === "number" ? order.couponDiscount : parseFloat(String(order.couponDiscount)))
      : 0;
    if (!isNaN(couponNum) && couponNum > 0) {
      const label = order.couponCode ? `Cupom (${order.couponCode}):` : "Cupom:";
      parts.push(padLine(label, `-${formatCurrency(couponNum)}`, width));
    }

    const loyaltyNum = receiptLoyaltyDiscount(order);
    if (loyaltyNum > 0) {
      parts.push(padLine(order.loyaltyLabel ? `Pontos (${order.loyaltyLabel}):` : "Pontos:", `-${formatCurrency(loyaltyNum)}`, width));
    }

    if (order.deliveryMode === "entrega") {
      const feeLabel = order.deliveryFeePending
        ? "a calcular"
        : order.deliveryFee > 0 ? formatCurrency(order.deliveryFee) : "Gratis";
      parts.push(padLine("Entrega:", feeLabel, width));
    }

    const surchargeNum = order.cardSurcharge != null
      ? (typeof order.cardSurcharge === "number" ? order.cardSurcharge : parseFloat(String(order.cardSurcharge)))
      : 0;
    if (!isNaN(surchargeNum) && surchargeNum > 0) {
      parts.push(padLine(`Acrescimo (${order.paymentMethod}):`, formatCurrency(surchargeNum), width));
    }
    const servico = taxaDeServico(order);
    // ESC/POS sem acento, como o «Acrescimo» acima.
    if (servico) parts.push(padLine(`${servico.rotulo.replace("ç", "c")}:`, formatCurrency(servico.valor), width));

    parts.push(
      ESC_POS.LF,
      ESC_POS.BOLD_ON,
      ESC_POS.DOUBLE_ON,
      padLine("TOTAL:", formatCurrency(order.total), width),
      corpo,
      ESC_POS.BOLD_OFF,
    );

    if (receiptShowsPaidCheckbox(order)) {
      parts.push(ESC_POS.BOLD_ON, padLine("", PAID_CHECKBOX_LABEL, width), ESC_POS.BOLD_OFF);
    }

    parts.push(sep());

    parts.push(line(`Pagamento: ${order.paymentMethod}`));
    if (Array.isArray(order.paymentParts) && order.paymentParts.length > 0) {
      for (const pp of order.paymentParts) {
        parts.push(line(`  ${pp.method}: ${formatCurrency(Number(pp.amount) || 0)}`));
      }
    }

    if (receiptHasCash(order)) {
      const cashSource = order.changeFor != null && order.changeFor !== ""
        ? order.changeFor
        : (order.cashReceived != null && order.cashReceived !== "" ? order.cashReceived : null);
      if (order.needsChange === false) {
        parts.push(line("Troco: Nao precisa"));
      } else if (cashSource != null) {
        const cashNum = typeof cashSource === "number" ? cashSource : parseFloat(String(cashSource));
        if (!isNaN(cashNum) && cashNum > 0) {
          parts.push(line(`Troco para: ${formatCurrency(cashNum)}`));
          const diff = Math.round((cashNum - receiptCashDue(order)) * 100) / 100;
          if (diff > 0) {
            parts.push(ESC_POS.BOLD_ON, line(`Troco a devolver: ${formatCurrency(diff)}`), ESC_POS.BOLD_OFF);
          }
        } else {
          parts.push(line(`Troco para: ${cashSource}`));
        }
      }
    }

    const credLine = receiptCrediarioLine(order);
    if (credLine) parts.push(line(credLine));

    parts.push(line(`Modalidade: ${receiptModeLabel(order)}${order.pickupMethod ? ` - ${order.pickupMethod}` : ""}`));
  }

  if (order.deliveryMode === "entrega" && order.address) {
    const a = order.address;
    let addrLine = [a.street, a.number].filter(Boolean).join(", ");
    if (a.complement) addrLine += ` - ${a.complement}`;
    parts.push(...wrapText(`End: ${addrLine}`, width));
    const addrLine2 = [a.neighborhood, a.city].filter(Boolean).join(" - ");
    if (addrLine2) parts.push(line(`     ${addrLine2}`));
    if (a.zipCode) parts.push(line(`     CEP: ${a.zipCode}`));
    if (a.reference) parts.push(...wrapText(`Ref: ${a.reference}`, width));
  }

  if (order.notes) {
    parts.push(sep());
    parts.push(ESC_POS.BOLD_ON, line("Observacoes:"), ESC_POS.BOLD_OFF);
    parts.push(...wrapText(order.notes, width));
  }

  if (order.birthdayMessage) {
    // Moldura de asteriscos no lugar do separador tracejado (escolha do
    // Pedro, 21/08): os asteriscos SÃO a borda de cima e de baixo.
    parts.push(ESC_POS.LF, sep("*"), ESC_POS.ALIGN_CENTER, ESC_POS.BOLD_ON);
    for (const parte of birthdayMessageLines(order.birthdayMessage, order.birthdayName, getCharsPerLine(width))) {
      for (const l of wrapText(parte, width)) parts.push(l);
    }
    parts.push(ESC_POS.BOLD_OFF, sep("*"), ESC_POS.ALIGN_CENTER, ESC_POS.LF);
  } else {
    parts.push(sep(), ESC_POS.ALIGN_CENTER, ESC_POS.LF);
  }
  // Rodape: 2 linhas de folga + corte que a PROPRIA impressora posiciona.
  // Antes eram LF + FEED_5 = 6 linhas em branco cravadas na mao, porque o
  // GS V 1 corta na posicao atual e sem essa folga a lamina passaria no texto.
  // So que a portatil barata NAO TEM guilhotina: ela ignora o corte e as 6
  // linhas viram um rabo de papel em branco (relato COMERCIAL FILADELFIA,
  // 22/08/2026). Com GS V B 0 quem tem lamina avanca sozinho ate a posicao
  // certa, e quem nao tem so gasta as 2 linhas que dao pra destacar na serrilha.
  parts.push(
    ...(order.footerMessage
      ? linhasDoRodape(order.footerMessage, getCharsPerLine(width)).map((l) => line(l))
      : [line("Obrigado pela preferencia!")]),
    ESC_POS.FEED_2,
    ESC_POS.CUT_FEED,
  );

  return buildReceipt(...parts);
}

function esc(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function formatReceiptHTML(
  storeName: string,
  order: ReceiptOrder,
): string {
  const topLeft = esc(receiptOrderLabel(order));
  const px = (n: number) => `${order.receiptFont === "alta" ? Math.round(n * 1.5) : n}px`;
  let html = `<div style="font-family:monospace;font-size:${px(12)};width:280px;margin:0 auto;padding:10px;">`;
  html += "<br/>".repeat(receiptTopMarginLines(order));
  html += `<div style="text-align:center;font-weight:bold;font-size:${px(16)};margin-bottom:8px;">${storeNameHeader(storeName, 32, 16).lines.map((l) => esc(l)).join("<br/>")}</div>`;
  html += `<hr/>`;
  html += `<div style="font-weight:bold;">${topLeft} — ${esc(formatDate(order.createdAt, order.timezone))}</div>`;
  if (order.tableNumber) html += `<div style="font-weight:bold;font-size:${px(15)};">${esc(order.tableLabel || "MESA")} ${esc(order.tableNumber)}</div>`;
  if (order.tableNumber && order.mesaDoCliente) html += `<div style="font-weight:bold;font-size:${px(15)};">MESA ${esc(order.mesaDoCliente)}</div>`;
  html += `<div>Cliente: ${esc(order.customerName)}</div>`;
  if (order.customerWhatsapp) html += `<div>WhatsApp: ${esc(order.customerWhatsapp)}</div>`;
  if (order.giftRecipient) html += `<div style="font-weight:bold;">Presente para: ${esc(order.giftRecipient)}</div>`;
  const dataUnicaHtml = receiptDataUnica(order.items);
  if (dataUnicaHtml) html += `<div style="font-weight:bold;">Encomenda: ${esc(dataUnicaHtml.data)}${esc(receiptQuandoEncomenda(dataUnicaHtml.hora, dataUnicaHtml.turno, true))}</div>`;
  html += `<hr/>`;

  for (const [idx, item] of order.items.entries()) {
    // Uma linha em branco entre os itens (T-0003, opção A).
    if (idx > 0) html += `<div>&nbsp;</div>`;
    html += order.semValores || receiptPrecoNaOpcao(item)
      ? `<div style="font-weight:bold;">${esc(receiptItemLabel(item))}</div>`
      : `<div style="font-weight:bold;">${esc(receiptItemLabel(item))} — ${esc(formatCurrency(item.subtotal))}</div>`;
    if (item.weightKg != null && item.weightKg > 0) {
      html += `<div style="padding-left:12px;">Peso: ${esc(receiptWeight(item.weightKg, item.weightUnit))}</div>`;
    }
    if (item.prepLine) for (const l of item.prepLine.split("\n")) html += `<div style="padding-left:12px;">${esc(l)}</div>`;
    if (item.options) {
      for (const grp of groupOptionsByTitle(item.options, item.quantity)) {
        if (grp.title) html += `<div style="padding-left:12px;font-weight:bold;margin-top:6px;">${esc(receiptGroupTitle(item, grp.title))}</div>`;
        for (const g of grp.options) {
          html += `<div style="padding-left:12px;">+ ${g.count > 1 ? `${g.count}x ` : ''}${esc(g.name)}${!order.semValores && g.price > 0 ? esc(receiptPrecoDaOpcao(item, g.price * g.count)) : ""}</div>`;
        }
      }
    }
    if (item.removedIngredients?.length) {
      html += `<div style="padding-left:12px;">Sem: ${esc(item.removedIngredients.join(", "))}</div>`;
    }
    if (item.scheduledDate && !dataUnicaHtml) {
      const [, m, d] = item.scheduledDate.split("-");
      html += `<div style="padding-left:12px;">Encomenda: ${esc(`${d}/${m}`)}${esc(receiptQuandoEncomenda(item.scheduledTime, item.scheduledShift, true))}</div>`;
    }
    if (item.notes) html += `<div style="padding-left:12px;">Obs: ${esc(item.notes)}</div>`;
  }

  if (!order.semValores) {
    html += `<hr/>`;
    html += `<div>Subtotal: ${esc(formatCurrency(order.subtotal))}</div>`;
    const couponNumHtml = order.couponDiscount != null
      ? (typeof order.couponDiscount === "number" ? order.couponDiscount : parseFloat(String(order.couponDiscount)))
      : 0;
    if (!isNaN(couponNumHtml) && couponNumHtml > 0) {
      html += `<div>Cupom${order.couponCode ? ` (${esc(order.couponCode)})` : ""}: -${esc(formatCurrency(couponNumHtml))}</div>`;
    }
    const loyaltyNumHtml = receiptLoyaltyDiscount(order);
    if (loyaltyNumHtml > 0) {
      html += `<div>Pontos${order.loyaltyLabel ? ` (${esc(order.loyaltyLabel)})` : ""}: -${esc(formatCurrency(loyaltyNumHtml))}</div>`;
    }
    if (order.deliveryMode === "entrega") {
      html += `<div>Entrega: ${order.deliveryFeePending ? "a calcular" : order.deliveryFee > 0 ? esc(formatCurrency(order.deliveryFee)) : "Grátis"}</div>`;
    }
    const surchargeNumHtml = order.cardSurcharge != null
      ? (typeof order.cardSurcharge === "number" ? order.cardSurcharge : parseFloat(String(order.cardSurcharge)))
      : 0;
    if (!isNaN(surchargeNumHtml) && surchargeNumHtml > 0) {
      html += `<div>Acréscimo (${esc(order.paymentMethod)}): ${esc(formatCurrency(surchargeNumHtml))}</div>`;
    }
    const servicoHtml = taxaDeServico(order);
    if (servicoHtml) html += `<div>${esc(servicoHtml.rotulo)}: ${esc(formatCurrency(servicoHtml.valor))}</div>`;
    html += `<div style="font-weight:bold;font-size:${px(14)};margin:8px 0;">TOTAL: ${esc(formatCurrency(order.total))}</div>`;
    if (receiptShowsPaidCheckbox(order)) {
      html += `<div style="font-weight:bold;text-align:right;">${esc(PAID_CHECKBOX_LABEL)}</div>`;
    }
    html += `<hr/>`;
    html += `<div>Pagamento: ${esc(order.paymentMethod)}</div>`;
    if (Array.isArray(order.paymentParts) && order.paymentParts.length > 0) {
      for (const pp of order.paymentParts) {
        html += `<div style="padding-left:8px;">${esc(pp.method)}: ${esc(formatCurrency(Number(pp.amount) || 0))}</div>`;
      }
    }
    if (receiptHasCash(order)) {
      const cashSource = order.changeFor != null && order.changeFor !== ""
        ? order.changeFor
        : (order.cashReceived != null && order.cashReceived !== "" ? order.cashReceived : null);
      if (order.needsChange === false) html += `<div>Troco: Não precisa</div>`;
      else if (cashSource != null) {
        const cfNum = typeof cashSource === "number" ? cashSource : parseFloat(String(cashSource));
        if (!isNaN(cfNum) && cfNum > 0) {
          html += `<div>Troco para: ${esc(formatCurrency(cfNum))}</div>`;
          const diff = Math.round((cfNum - receiptCashDue(order)) * 100) / 100;
          if (diff > 0) html += `<div style="font-weight:bold;">Troco a devolver: ${esc(formatCurrency(diff))}</div>`;
        } else {
          html += `<div>Troco para: ${esc(String(cashSource))}</div>`;
        }
      }
    }
    {
      const credLine = receiptCrediarioLine(order);
      if (credLine) html += `<div>${esc(credLine)}</div>`;
    }
    html += `<div>Modalidade: ${receiptModeLabel(order)}${order.pickupMethod ? ` - ${esc(order.pickupMethod)}` : ""}</div>`;
  }

  if (order.deliveryMode === "entrega" && order.address) {
    const a = order.address;
    let addrStr = [a.street, a.number].filter(Boolean).join(", ");
    if (a.complement) addrStr += ` - ${a.complement}`;
    html += `<div>End: ${esc(addrStr)}</div>`;
    const line2 = [a.neighborhood, a.city].filter(Boolean).join(" - ");
    if (line2) html += `<div>${esc(line2)}</div>`;
    if (a.zipCode) html += `<div>CEP: ${esc(a.zipCode)}</div>`;
    if (a.reference) html += `<div>Ref: ${esc(a.reference)}</div>`;
  }

  if (order.notes) {
    html += `<hr/><div style="font-weight:bold;">Observações:</div><div>${esc(order.notes)}</div>`;
  }

  if (order.birthdayMessage) {
    const stars = `<div style="text-align:center;overflow:hidden;white-space:nowrap;">${"*".repeat(32)}</div>`;
    html += `<div style="margin-top:16px;">${stars}<div style="text-align:center;font-weight:bold;">${esc(order.birthdayMessage)}</div>${stars}</div>`;
  }
  html += order.footerMessage
    ? `<hr/><div style="text-align:center;margin-top:8px;white-space:pre-line;overflow-wrap:anywhere;">${esc(order.footerMessage)}</div>`
    : `<hr/><div style="text-align:center;margin-top:8px;">Obrigado pela preferência!</div>`;
  html += `</div>`;
  return html;
}

/** Desconto dos prêmios de pontos do cupom (0 quando não há). */
export function receiptLoyaltyDiscount(order: { loyaltyDiscount?: number | string | null }): number {
  const n = order.loyaltyDiscount != null
    ? (typeof order.loyaltyDiscount === "number" ? order.loyaltyDiscount : parseFloat(String(order.loyaltyDiscount)))
    : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// opts.hideDailyNumber: a loja desligou "imprimir o número do pedido" — o
// cupom não deve mostrar o número (esconde a contagem de pedidos do cliente).
// opts.timezone: fuso da loja pra data/hora impressa (default BRT).
// opts.hideGroupTitles: a loja desligou os títulos dos grupos de adicionais —
// zera o groupName e os formatters caem no layout compacto sem cabeçalho.
// opts.orderCodeStyle: "numero" (nº do dia, default) | "codigo" (fim do UUID,
// estilo antigo). Com "codigo" o dailyOrderNumber sai null do payload pra
// agente antigo não imprimir o número que a loja escolheu não usar.
// opts.porCliente: a loja usa a comanda individual («1 QR code para cada cliente»): o número sai como «COMANDA 12».
export function orderToReceipt(order: any, opts?: { hideDailyNumber?: boolean; timezone?: string | null; hideGroupTitles?: boolean; orderCodeStyle?: string | null; topMargin?: number | null; showPaidCheckbox?: boolean; birthdayMessage?: string | null; birthdayName?: string | null; receiptFont?: string | null; footerMessage?: string | null; porCliente?: boolean }): ReceiptOrder {
  const items = (order.items ?? []).map((item: any) => {
    const price = parseFloat(item.price || "0");
    const optionsTotal = parseFloat(item.optionsTotal || "0");
    const qty = parseInt(item.quantity || "1");
    const options = item.selectedOptions || [];
    // «Cada lista conta separado»: a conta vai no título de cada lista, e os títulos ficam mesmo com a loja escondendo os
    // títulos dos grupos (sem eles, não dá para saber qual adicional vai em qual copo). Fora do título, só sai a lista
    // que ficou sem escolha.
    const prepPorGrupo = receiptPrepPorGrupo(item.prepCount);
    const comEscolha = new Set(options.map((o: any) => String(o?.groupName ?? "")));
    return {
      name: item.productName || item.name || "Produto",
      quantity: qty,
      unitPrice: price + optionsTotal,
      subtotal: (price + optionsTotal) * qty,
      basePrice: price,
      options: opts?.hideGroupTitles && !prepPorGrupo
        ? options.map((o: any) => ({ ...o, groupName: null }))
        : options,
      removedIngredients: item.removedIngredients || [],
      scheduledDate: item.scheduledDate || null,
      scheduledTime: item.scheduledTime || null,
      scheduledShift: item.scheduledShift || null,
      notes: item.notes || null,
      weightKg:
        item.weightKg != null && parseFloat(String(item.weightKg)) > 0
          ? parseFloat(String(item.weightKg))
          : null,
      weightUnit: item.weightUnit ?? null,
      prepLine: prepPorGrupo
        ? (() => {
            // Só as listas sem escolha (as outras já saem no título); nenhuma = sem linha (a soma não diz o copo).
            const semEscolha = item.prepCount.listas.filter((l: any) => !comEscolha.has(String(l?.nome ?? "")));
            return semEscolha.length ? receiptPrepLine({ ...item.prepCount, listas: semEscolha }) : null;
          })()
        : receiptPrepLine(item.prepCount),
      prepPorGrupo,
    };
  });
  const subtotal = items.reduce((s: number, i: any) => s + i.subtotal, 0);
  const itemsExibidos = expandItemUnits(items);
  const deliveryFee = parseFloat(order.deliveryFee || "0");
  const style = opts?.orderCodeStyle === "codigo" ? "codigo" : "numero";
  return {
    id: order.id,
    topMargin: opts?.topMargin ?? null,
    dailyOrderNumber:
      opts?.hideDailyNumber || style === "codigo" ? null : (order.dailyOrderNumber ?? null),
    orderCode: opts?.hideDailyNumber
      ? null
      : style === "codigo"
        ? String(order.id ?? "").slice(-4).toUpperCase()
        : order.dailyOrderNumber != null
          ? String(order.dailyOrderNumber).padStart(4, "0")
          : null,
    createdAt: order.createdAt,
    customerName: order.customer?.firstName || "Cliente",
    customerWhatsapp: receiptWhatsapp(order.customer?.whatsappNumber),
    giftRecipient: order.giftRecipient || null,
    deliveryMode: order.deliveryMode || "retirada",
    pickupMethod: order.pickupMethod ?? null,
    paymentMethod: order.paymentMethod || "",
    items: itemsExibidos,
    subtotal,
    deliveryFee,
    deliveryFeePending: order.deliveryFeePending === true,
    total: parseFloat(order.total || "0"),
    address: order.address || null,
    notes: order.notes || null,
    needsChange: typeof order.needsChange === "boolean" ? order.needsChange : undefined,
    changeFor: order.changeFor != null ? parseFloat(String(order.changeFor)) : null,
    cashReceived: order.cashReceived != null ? parseFloat(String(order.cashReceived)) : null,
    cardSurcharge: order.cardSurcharge != null ? parseFloat(String(order.cardSurcharge)) : null,
    taxaServico: order.taxaServico != null ? parseFloat(String(order.taxaServico)) : null,
    taxaServicoPct: order.taxaServicoPct != null ? parseFloat(String(order.taxaServicoPct)) : null,
    couponCode: order.couponCode ?? null,
    couponDiscount: order.couponDiscount != null ? parseFloat(String(order.couponDiscount)) : null,
    loyaltyDiscount: order.loyaltyDiscount != null ? parseFloat(String(order.loyaltyDiscount)) : null,
    loyaltyLabel: Array.isArray(order.loyaltyRewards)
      ? order.loyaltyRewards
          .filter((r: any) => Number(r?.discount) > 0)
          .map((r: any) => String(r?.name ?? ""))
          .filter(Boolean)
          .join(", ") || null
      : null,
    paymentParts: Array.isArray(order.paymentParts) ? order.paymentParts : null,
    crediarioInstallments: order.crediarioInstallments ?? null,
    crediarioFirstDue: order.crediarioFirstDue ?? null,
    timezone: opts?.timezone ?? null,
    tableNumber: order.tableNumber ?? null,
    semValores: !!order.tableNumber && order.paymentMethod === "Pendente",
    tableLabel: opts?.porCliente ? "COMANDA" : "MESA",
    mesaDoCliente: opts?.porCliente ? (order.mesaDoCliente ?? null) : null,
    showPaidCheckbox: opts?.showPaidCheckbox === true,
    birthdayMessage: opts?.birthdayMessage ?? null,
    birthdayName: opts?.birthdayName ?? null,
    receiptFont: opts?.receiptFont === "alta" ? "alta" : null,
    footerMessage: normalizarRodape(opts?.footerMessage) || null,
  };
}

// Opção que não cabe na linha: quebra entre palavras, com o preço inteiro e a continuação recuada sob o nome (a impressora
// cortava no meio do preço: «+R$ 5» / «5,00»; Pizzaria Potiguar, 20/10/2026).
export function linhasDeOpcao(nome: string, preco: string, cols: number): string[] {
  const ini = "   + ", rec = "     ";
  const tokens = nome.split(" ").filter(Boolean);
  if (preco.trim()) tokens.push(preco.trim());
  const out: string[] = [];
  let atual = ini;
  for (const t of tokens) {
    const vazio = atual === ini || atual === rec;
    if (vazio) atual += t;
    else if ((atual + " " + t).length <= cols) atual += " " + t;
    else {
      out.push(atual);
      atual = rec + t;
    }
  }
  out.push(atual);
  return out;
}
